import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';
import SearchableSelect from '../components/SearchableSelect';
import LoginGate from '../components/LoginGate';
import { useAuth } from '../App';
import { Search, MapPin, GraduationCap, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UniversitiesListPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [unis, setUnis] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [country, setCountry] = useState(params.get('country') || '');

  useEffect(() => {
    const load = async () => {
      try {
        const [u, c] = await Promise.all([
          axios.get(`${API}/public/master-data?type=university`),
          axios.get(`${API}/public/master-data?type=country`),
        ]);
        setUnis(Array.isArray(u.data) ? u.data : []);
        setCountries(Array.isArray(c.data) ? c.data : []);
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return unis.filter((u) => {
      const matchQ = !qq || (u.name || '').toLowerCase().includes(qq);
      const matchC = !country || (u.country || '').toLowerCase() === country.toLowerCase();
      return matchQ && matchC;
    });
  }, [unis, q, country]);

  const showGate = !authLoading && !user;
  return (
    <PublicLayout>
      <div className={showGate ? 'pointer-events-none select-none blur-[3px]' : ''}>
      <PageHero title="Partner Universities" subtitle="Browse our network of accredited universities across 10+ countries." />

      <section className="max-w-7xl mx-auto px-4 -mt-10 relative z-10">
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5 flex flex-col md:flex-row gap-3" data-testid="uni-filters">
          <div className="flex-1 flex items-center border border-gray-200 rounded-md px-3 py-2.5">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search universities..."
              className="ml-2 flex-1 outline-none text-sm"
              data-testid="uni-search-input"
            />
          </div>
          <div className="flex items-center md:w-64">
            <SearchableSelect
              value={country}
              onChange={setCountry}
              options={countries.map((c) => ({ value: c.name, label: c.name }))}
              placeholder="All countries"
              testid="uni-country-filter"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-10">
        {loading ? (
          <p className="text-center text-gray-500">Loading universities…</p>
        ) : filtered.length === 0 ? (
          <EmptyState label="No universities found yet. Check back soon." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="uni-grid">
            {filtered.map((u) => (
              <div key={u.id || u.name} className="group bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-[#F5A623] hover:shadow-lg transition" data-testid={`uni-card-${u.id || u.name}`}>
                <div className="h-32 bg-gradient-to-br from-[#1A2A4F] to-[#3a4d7f] flex items-center justify-center">
                  {u.logo_url ? (
                    <img src={u.logo_url} alt={u.name} className="max-h-20 max-w-[80%] object-contain" />
                  ) : (
                    <GraduationCap className="w-14 h-14 text-[#F5A623]" />
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#1A2A4F] line-clamp-2">{u.name}</h3>
                  {u.country && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500"><MapPin className="w-3 h-3" />{u.country}</p>
                  )}
                  <Link to={`/courses?university=${encodeURIComponent(u.name)}`} className="mt-4 inline-flex text-sm font-semibold text-[#F5A623] hover:underline" data-testid={`uni-view-courses-${u.id || u.name}`}>
                    View courses →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      </div>
      {showGate && (
        <LoginGate
          title="Sign in to view partner universities"
          subtitle="Our university listings are tailored to your eligibility and country preferences. Create a free account to see the full list."
          returnTo={`/universities?${params.toString()}`}
        />
      )}
    </PublicLayout>
  );
};

const PageHero = ({ title, subtitle }) => (
  <section className="bg-gradient-to-br from-[#1A2A4F] to-[#2d4373] text-white">
    <div className="max-w-7xl mx-auto px-4 py-14 sm:py-20">
      <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">{title}</h1>
      {subtitle && <p className="mt-3 text-base sm:text-lg opacity-90 max-w-2xl">{subtitle}</p>}
    </div>
  </section>
);

const EmptyState = ({ label }) => (
  <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl py-16 px-4 text-center">
    <p className="text-gray-500">{label}</p>
  </div>
);

export { PageHero, EmptyState };
export default UniversitiesListPage;
